import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { usePostHog } from "posthog-js/react";
import Modal from "@/components/overlays/Modal";
import { ExclamationCircleIcon, XCircleIcon } from "@heroicons/react/outline";
import WhatsAppIcon from "../contact/img/whatsapp.svg";
import CircleSteps from "../../../src/components/navigation/CircleSteps";
import ProgressBar from "../../../src/components/navigation/ProgressBar";
import acquisitionClient from "../../services/acquisitionApiClient";
import { authUser } from "@/_actions/authActions";
import apiClient from "@/utils/apiClient";
import SignupLogo from "/public/images/signup-logo.svg";

const particularSteps = [
  {
    name: "Tipo de cuenta",
    description: "Particular",
    href: "#",
    status: "current",
  },
  {
    name: "Ubicación y precio",
    description: "Define el tipo de inmueble y operación",
    href: "#",
    status: "upcoming",
  },
  {
    name: "Amenidades",
    description: "¿Qué pueden encontrar en la propiedad?",
    href: "#",
    status: "upcoming",
  },
  {
    name: "Descripción y fotos",
    description: "Detalles más específicos",
    href: "#",
    status: "upcoming",
  },
  {
    name: "Pago",
    description: "Contrata tu plan y accede a PRO",
    href: "#",
    status: "upcoming",
  },
];

const Register = () => {
  const [viewNotification, toggleNotification] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const navigate = useNavigate();
  const posthog = usePostHog();
  const urlParams = new URLSearchParams(window.location.search);
  const plan = urlParams.get("plan");
  if (plan) {
    localStorage.setItem("selectedPlan", plan);
  }

  useEffect(() => {
    if (posthog) {
      posthog.capture("Signup - Particular", { step: 0, stepName: "started" });
    }
  }, [posthog]);

  async function getUserData() {
    try {
      const res = await apiClient.get("/accounts/me/");
      setUserReport(res.data[0]);
    } catch (err) {
      $log.error(err);
    }
  }

  function setUserReport(user) {
    try {
      if (typeof window !== "undefined") {
        if (window?.ga) {
          ga("set", "userId", user.id);
        }
      }
    } catch (err) {
      $log.error(err);
    }
  }

  const onSubmit = async (values, actions) => {
    const { setSubmitting } = actions;
    setSubmitting(true);

    try {
      const payload = {
        ...values,
        client_type: "property_owner",
      };

      await acquisitionClient.post("/customers/auto-registration/", payload);

      // Sign the user into Cognito so apiClient-bound calls in later wizard
      // steps (useStates, useMunicipalities, etc.) work. The verify-email
      // tab inherits the Amplify session via shared localStorage.
      await authUser(values.email, values.password);
      await getUserData();

      // Marker used by verify-email to detect whether the verification link
      // was opened on the same browser the signup started in.
      localStorage.setItem("signup_pending", "true");

      setRegisteredEmail(values.email);
      setShowVerifyModal(true);

      if (posthog) {
        posthog.capture("Signup - Particular", {
          step: 1,
          stepName: "account",
          email: values.email,
        });
      }
    } catch (err) {
      if (err?.response?.status === 409) {
        toggleNotification(true);
      }
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const SignupSchema = Yup.object().shape({
    name: Yup.string().required("Campo requerido"),
    last_name: Yup.string().required("Campo requerido"),
    email: Yup.string().email("Correo electrónico no válido").required("Campo requerido"),
    password: Yup.string()
      .min(8, "La contraseña debe tener al menos 8 caracteres")
      .required("Campo requerido"),
    city: Yup.string().required("Campo requerido"),
    phone: Yup.string().length(10, "Teléfono a 10 dígitos").required("Campo requerido"),
    client_name: Yup.string(),
  });

  return (
    <>
      <div className="relative">
        <div className="space-y-1 bg-gray-100 p-3 md:hidden ">
          <p className="text-center font-semibold uppercase leading-4">Datos de contacto</p>
          <p className="text-center">Información personal y de contacto</p>
          <ProgressBar
            config={{
              step: 1,
              total: 5,
            }}
          />
        </div>
        <div className="grid-cols-3 md:grid  ">
          <div className="col-span-1 hidden min-h-screen bg-gray-100 pt-16 md:block ">
            <div className="m-auto block w-3/4 space-y-3">
              <p className="text-5xl font-extrabold leading-none text-black">
                Publica en Casas y Terrenos
              </p>
              <p className="text-lg font-semibold uppercase leading-6 tracking-wider">
                Toma al rededor de 7 minutos
              </p>
              <p className="pb-4 text-lg leading-8 tracking-wider">Contrata desde $600 + iva</p>
              <div className="hidden md:block">
                <CircleSteps steps={particularSteps} />
              </div>
            </div>
          </div>
          <div className="col-span-2 block ">
            <div className="m-auto flex w-full px-5 pt-16 md:min-h-screen md:w-3/4 lg:w-2/3 ">
              <Formik
                onSubmit={onSubmit}
                initialValues={{
                  client_name: "",
                  name: "",
                  last_name: "",
                  email: "",
                  password: "",
                  city: "",
                  phone: "",
                }}
                validationSchema={SignupSchema}
              >
                {({ values, errors, touched, isSubmitting }) => (
                  <Form className="relative w-full space-y-3">
                    <p className="form-title my-8">Datos de contacto</p>
                    <div className="grid-cols-2 space-y-3 md:grid md:gap-6 md:space-y-0">
                      <div>
                        <label htmlFor="name" className="font-medium">
                          Nombre
                        </label>
                        <Field
                          id="name"
                          name="name"
                          type="text"
                          className="input"
                          placeholder="Nombre"
                        />
                        {errors.name && touched.name && (
                          <div className="text-sm text-red-600">{errors.name}</div>
                        )}
                      </div>

                      <div>
                        <label htmlFor="last_name" className="font-medium">
                          Apellido
                        </label>
                        <Field
                          id="last_name"
                          name="last_name"
                          type="text"
                          className="input"
                          placeholder="Apellido"
                        />
                        {errors.last_name && touched.last_name && (
                          <div className="text-sm text-red-600">{errors.last_name}</div>
                        )}
                      </div>

                      <div>
                        <label htmlFor="email" className="font-medium">
                          Correo electrónico
                        </label>
                        <Field
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          className="input"
                          placeholder="you@example.com"
                        />
                        {errors.email && touched.email && (
                          <div className="text-sm text-red-600">{errors.email}</div>
                        )}
                      </div>

                      <div>
                        <label htmlFor="password" className="font-medium">
                          Contraseña
                        </label>
                        <Field
                          id="password"
                          name="password"
                          type="password"
                          autoComplete="password"
                          className="input"
                          placeholder="********"
                        />
                        {errors.password && touched.password && (
                          <div className="text-sm text-red-600">{errors.password}</div>
                        )}
                      </div>

                      <div>
                        <label htmlFor="phone" className="font-medium">
                          Teléfono
                        </label>
                        <Field
                          id="phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          className="input"
                          placeholder="000-000-0000"
                        />
                        {errors.phone && touched.phone && (
                          <div className="text-sm text-red-600">{errors.phone}</div>
                        )}
                      </div>

                      <div>
                        <label htmlFor="city" className="font-medium">
                          Ciudad
                        </label>
                        <Field
                          id="city"
                          name="city"
                          type="text"
                          autoComplete="address-level2"
                          className="input"
                          placeholder="Ciudad"
                        />
                        {errors.city && touched.city && (
                          <div className="text-sm text-red-600">{errors.city}</div>
                        )}
                      </div>
                    </div>

                    <div className="sticky bottom-0 col-span-2 mt-3 w-full bg-white p-4 md:block">
                      <div className="mb-10 hidden items-center md:flex" aria-hidden="true">
                        <div className="w-full border-t border-gray-300"></div>
                      </div>
                      <div className="flex justify-center mb-4">
                        <Link to="/login" className="text-black font-bold hover:underline text-lg">
                          Ya tengo cuenta
                        </Link>
                      </div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="focus:outline-none group relative mb-6 flex w-full justify-center rounded-md border border-transparent bg-black px-4 py-2 text-lg font-medium text-white hover:bg-gray-800 focus:ring-2 focus:ring-black focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                          <svg
                            className="h-5 w-5 text-white group-hover:text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                        {isSubmitting ? "Enviando..." : "Continuar"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>

              <Modal open={viewNotification} setOpen={toggleNotification} width="sm:max-w-[500px]">
                <div className="flex w-full flex-col items-center px-3 pb-14 pt-3 text-center md:px-5 md:pb-7">
                  <div className="flex w-full justify-center">
                    <ExclamationCircleIcon className="h-20 w-20 text-red-700" aria-hidden="true" />
                  </div>
                  <div className="px-3">
                    <h3 className="py-3 font-bold">Correo electrónico en uso</h3>
                    El correo electrónico ingresado ya esta registrado.
                    <br />
                    Utiliza un correo electrónico diferente o comunícate con una de nuestras
                    ejecutivas.
                    <span className="py-5 font-bold">
                      <br />
                      Email: pelizalde@casasyterrenos.com
                      <br />
                      Teléfono: 33 1715 0016
                    </span>
                  </div>
                  <button
                    onClick={() =>
                      window.open(
                        "https://api.whatsapp.com/send?phone=+523317150016&text=Hola, no puedo crear una cuenta por que mi correo ya esta en uso.",
                        "_blank",
                      )
                    }
                    className="mt-4 flex h-10 w-full items-center justify-items-center rounded bg-green-whatsapp text-white hover:bg-green-whatsapp-darker"
                  >
                    <div className="m-auto flex md:px-2">
                      <img
                        alt="ícono whatsapp"
                        className="mx-2 w-4"
                        referrerPolicy="no-referrer-when-downgrade"
                        src={WhatsAppIcon}
                      />
                      <span className="mx-2"> Obtener mas información</span>
                    </div>
                  </button>
                </div>
              </Modal>
              <Modal open={showVerifyModal} setOpen={setShowVerifyModal}>
                <div className="flex flex-col items-center justify-center p-8 text-center">
                  <img src={SignupLogo} alt="Signup Logo" className="w-40 h-40 mb-4" />
                  <h2 className="text-2xl font-bold mb-4">Confirma tu cuenta</h2>
                  <p className="text-gray-600">Te enviamos un enlace de validación al correo</p>
                  <p className="text-gray-900 font-medium mb-6">{registeredEmail}</p>
                  <p className="text-gray-600 mb-8">
                    Haz clic en él para empezar a usar tu plataforma PRO.
                  </p>
                  <button
                    className="rounded-full px-8 py-3 text-base border border-gray-300 hover:bg-gray-100"
                    onClick={() => setShowVerifyModal(false)}
                  >
                    Cerrar
                  </button>
                </div>
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Register;
